import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Layout from "../../../layout/Layout";

import {
  seedDefaultQualityFolders,
  createQualityFolder,
  getQualityFolderTree,
  updateQualityFolder,
  deleteQualityFolder,
  createQualityDocument,
  getQualityDocumentList,
  deleteQualityDocument,
} from "../../../../api/kalite/qmad/qmadApi";
const DOCUMENT_TYPES = [
  { value: "", label: "Tümü" },
  { value: "FORM", label: "Form" },
  { value: "PROCEDURE", label: "Prosedür" },
  { value: "INSTRUCTION", label: "Talimat" },
  { value: "STANDARD", label: "Standart" },
  { value: "RECORD", label: "Kayıt" },
  { value: "REPORT", label: "Rapor" },
  { value: "CERTIFICATE", label: "Sertifika" },
  { value: "POLICY", label: "Politika" },
  { value: "OTHER", label: "Diğer" },
];

const DOCUMENT_STATUSES = [
  { value: "", label: "Tümü" },
  { value: "DRAFT", label: "Taslak" },
  { value: "IN_REVIEW", label: "İncelemede" },
  { value: "PUBLISHED", label: "Yayında" },
  { value: "ARCHIVED", label: "Arşiv" },
  { value: "PASSIVE", label: "Pasif" },
];

const STATUS_BADGE = {
  DRAFT: "secondary",
  IN_REVIEW: "warning",
  PUBLISHED: "success",
  ARCHIVED: "dark",
  PASSIVE: "danger",
};

const getPayload = (response) => response?.data || response || {};

function QualityFileManagerPage() {
  const navigate = useNavigate();

  const [tokenReady, setTokenReady] = useState(false);

  const [loading, setLoading] = useState(false);
  const [folderLoading, setFolderLoading] = useState(false);
  const [documentLoading, setDocumentLoading] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [folderTree, setFolderTree] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);

  const [documents, setDocuments] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 25,
  });

  const [filters, setFilters] = useState({
    search: "",
    documentType: "",
    status: "",
    includeSubFolders: false,
  });

  const [folderModal, setFolderModal] = useState({
    show: false,
    mode: "create",
    parentId: null,
    folderId: null,
    name: "",
    description: "",
    order: 0,
  });

  const [documentModal, setDocumentModal] = useState({
    show: false,
    folderId: "",
    documentCode: "",
    documentName: "",
    documentType: "OTHER",
    status: "DRAFT",
    revisionNo: 0,
    revisionDate: "",
    description: "",
    changeNote: "",
    file: null,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const token = sessionStorage.getItem("token");
      if (token) {
        setTokenReady(true);
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  const selectedFolderId = selectedFolder?._id || "";

  const loadFolders = useCallback(async () => {
    try {
      setFolderLoading(true);
      setError("");

      const response = await getQualityFolderTree({
        department: "Kalite",
        includePassive: false,
      });

      const payload = getPayload(response);

      setFolderTree(payload.tree || []);
      setFolders(payload.folders || []);

      if (!selectedFolder && payload.tree?.length > 0) {
        setSelectedFolder(payload.tree[0]);
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Klasörler alınamadı.");
    } finally {
      setFolderLoading(false);
    }
  }, [selectedFolder]);

  const loadDocuments = useCallback(
    async (page = 1) => {
      try {
        setDocumentLoading(true);
        setError("");

        const response = await getQualityDocumentList({
          page,
          limit: pagination.limit,
          folderId: selectedFolderId || undefined,
          includeSubFolders: filters.includeSubFolders,
          search: filters.search,
          documentType: filters.documentType || undefined,
          status: filters.status || undefined,
          department: "Kalite",
          sortBy: "createdAt",
          sortDir: "desc",
        });

        const payload = getPayload(response);

        setDocuments(payload.documents || []);
        setPagination({
          currentPage: payload.currentPage || page,
          totalPages: payload.totalPages || 1,
          totalRecords: payload.totalRecords || 0,
          limit: payload.limit || 25,
        });
      } catch (err) {
        setError(err?.response?.data?.message || err.message || "Dokümanlar alınamadı.");
      } finally {
        setDocumentLoading(false);
      }
    },
    [
      filters.documentType,
      filters.includeSubFolders,
      filters.search,
      filters.status,
      pagination.limit,
      selectedFolderId,
    ]
  );

  useEffect(() => {
    if (!tokenReady) return;
    loadFolders();
  }, [tokenReady, loadFolders]);

  useEffect(() => {
    if (!tokenReady) return;
    loadDocuments(1);
  }, [
    tokenReady,
    selectedFolderId,
    filters.documentType,
    filters.status,
    filters.includeSubFolders,
    loadDocuments,
  ]);

  const folderOptions = useMemo(() => {
    return folders.map((folder) => ({
      value: folder._id,
      label: folder.path || folder.name,
    }));
  }, [folders]);

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const openCreateRootFolderModal = () => {
    clearMessages();
    setFolderModal({
      show: true,
      mode: "create",
      parentId: null,
      folderId: null,
      name: "",
      description: "",
      order: 0,
    });
  };

  const openCreateChildFolderModal = (parentFolder) => {
    clearMessages();
    setFolderModal({
      show: true,
      mode: "create",
      parentId: parentFolder?._id || selectedFolder?._id || null,
      folderId: null,
      name: "",
      description: "",
      order: 0,
    });
  };

  const openEditFolderModal = (folder) => {
    clearMessages();
    setFolderModal({
      show: true,
      mode: "edit",
      parentId: folder.parentId || null,
      folderId: folder._id,
      name: folder.name || "",
      description: folder.description || "",
      order: folder.order || 0,
    });
  };

  const closeFolderModal = () => {
    setFolderModal((prev) => ({ ...prev, show: false }));
  };

  const submitFolder = async (event) => {
    event.preventDefault();

    try {
      setLoading(true);
      clearMessages();

      if (folderModal.mode === "edit") {
        await updateQualityFolder({
          folderId: folderModal.folderId,
          name: folderModal.name,
          parentId: folderModal.parentId,
          description: folderModal.description,
          order: folderModal.order,
        });

        setSuccess("Klasör güncellendi.");
      } else {
        await createQualityFolder({
          name: folderModal.name,
          parentId: folderModal.parentId,
          description: folderModal.description,
          order: folderModal.order,
          department: "Kalite",
        });

        setSuccess("Klasör oluşturuldu.");
      }

      closeFolderModal();
      await loadFolders();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Klasör işlemi başarısız.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFolder = async (folder) => {
    const confirmed = window.confirm(
      `"${folder.name}" klasörü pasife alınacak. Emin misin?`
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      clearMessages();

      await deleteQualityFolder({
        folderId: folder._id,
      });

      setSuccess("Klasör pasife alındı.");

      if (selectedFolder?._id === folder._id) {
        setSelectedFolder(null);
      }

      await loadFolders();
      await loadDocuments(1);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Klasör pasife alınamadı.");
    } finally {
      setLoading(false);
    }
  };

  const openDocumentModal = () => {
    clearMessages();

    setDocumentModal({
      show: true,
      folderId: selectedFolder?._id || "",
      documentCode: "",
      documentName: "",
      documentType: "OTHER",
      status: "DRAFT",
      revisionNo: 0,
      revisionDate: "",
      description: "",
      changeNote: "",
      file: null,
    });
  };

  const closeDocumentModal = () => {
    setDocumentModal((prev) => ({ ...prev, show: false }));
  };

  const submitDocument = async (event) => {
    event.preventDefault();

    try {
      setLoading(true);
      clearMessages();

      if (!documentModal.folderId) {
        setError("Klasör seçmelisin.");
        return;
      }

      if (!documentModal.file) {
        setError("Dosya seçmelisin.");
        return;
      }

      const formData = new FormData();

      formData.append("folderId", documentModal.folderId);
      formData.append("documentCode", documentModal.documentCode);
      formData.append("documentName", documentModal.documentName);
      formData.append("documentType", documentModal.documentType);
      formData.append("status", documentModal.status);
      formData.append("revisionNo", documentModal.revisionNo);
      formData.append("revisionDate", documentModal.revisionDate);
      formData.append("description", documentModal.description);
      formData.append("changeNote", documentModal.changeNote);
      formData.append("file", documentModal.file);

      await createQualityDocument(formData);

      setSuccess("Doküman yüklendi.");
      closeDocumentModal();
      await loadDocuments(1);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Doküman yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (document) => {
    const confirmed = window.confirm(
      `"${document.documentName}" dokümanı pasife alınacak. Emin misin?`
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      clearMessages();

      await deleteQualityDocument({
        documentId: document._id,
      });

      setSuccess("Doküman pasife alındı.");
      await loadDocuments(pagination.currentPage);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Doküman pasife alınamadı.");
    } finally {
      setLoading(false);
    }
  };

  const handleSeedFolders = async () => {
    try {
      setLoading(true);
      clearMessages();

      await seedDefaultQualityFolders({});
      setSuccess("Varsayılan kalite klasörleri oluşturuldu.");
      await loadFolders();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Varsayılan klasörler oluşturulamadı.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    loadDocuments(1);
  };

  const renderFolderTree = (items, depth = 0) => {
    if (!items || items.length === 0) return null;

    return items.map((folder) => {
      const isSelected = selectedFolder?._id === folder._id;

      return (
        <div key={folder._id}>
          <div
            className={`d-flex align-items-center justify-content-between px-2 py-2 rounded ${
              isSelected ? "bg-primary text-white" : "bg-light"
            }`}
            style={{
              marginLeft: depth * 14,
              marginBottom: 6,
              cursor: "pointer",
            }}
            onClick={() => setSelectedFolder(folder)}
          >
            <div className="text-truncate">
              <span className="me-2">📁</span>
              <strong>{folder.name}</strong>
            </div>

            <ButtonGroup size="sm" onClick={(e) => e.stopPropagation()}>
              <Button
                variant={isSelected ? "light" : "outline-primary"}
                onClick={() => openCreateChildFolderModal(folder)}
                title="Alt klasör ekle"
              >
                +
              </Button>
              <Button
                variant={isSelected ? "light" : "outline-secondary"}
                onClick={() => openEditFolderModal(folder)}
                title="Düzenle"
              >
                ✎
              </Button>
              {!folder.isSystemFolder && (
                <Button
                  variant={isSelected ? "light" : "outline-danger"}
                  onClick={() => handleDeleteFolder(folder)}
                  title="Pasife al"
                >
                  ×
                </Button>
              )}
            </ButtonGroup>
          </div>

          {folder.children?.length > 0 && renderFolderTree(folder.children, depth + 1)}
        </div>
      );
    });
  };

  return (
    <Layout>
      <div className="container-fluid py-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-0">Kalite Dosya Yönetimi</h4>
            <small className="text-muted">
              QMAD benzeri klasör ağacı, doküman ve revizyon yönetimi
            </small>
          </div>

          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={handleSeedFolders} disabled={loading}>
              Varsayılan Klasörleri Kur
            </Button>
            <Button variant="outline-primary" onClick={openCreateRootFolderModal}>
              + Klasör
            </Button>
            <Button variant="primary" onClick={openDocumentModal}>
              + Doküman Yükle
            </Button>
          </div>
        </div>

        {error && (
          <Alert variant="danger" onClose={() => setError("")} dismissible>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" onClose={() => setSuccess("")} dismissible>
            {success}
          </Alert>
        )}

        <Row>
          <Col lg={3}>
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <strong>Klasörler</strong>
                {folderLoading && <Spinner size="sm" />}
              </Card.Header>

              <Card.Body style={{ maxHeight: "72vh", overflowY: "auto" }}>
                {folderTree.length === 0 ? (
                  <div className="text-muted small">
                    Henüz klasör yok. “Varsayılan Klasörleri Kur” ile başlayabilirsin.
                  </div>
                ) : (
                  renderFolderTree(folderTree)
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col lg={9}>
            <Card>
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>Dokümanlar</strong>
                    {selectedFolder && (
                      <div className="small text-muted">
                        Seçili klasör: {selectedFolder.path || selectedFolder.name}
                      </div>
                    )}
                  </div>

                  <Badge bg="secondary">{pagination.totalRecords} kayıt</Badge>
                </div>
              </Card.Header>

              <Card.Body>
                <Form onSubmit={handleSearchSubmit} className="mb-3">
                  <Row className="g-2">
                    <Col md={4}>
                      <InputGroup>
                        <Form.Control
                          placeholder="Doküman adı, kodu, açıklama ara..."
                          value={filters.search}
                          onChange={(e) =>
                            setFilters((prev) => ({
                              ...prev,
                              search: e.target.value,
                            }))
                          }
                        />
                        <Button variant="outline-primary" type="submit">
                          Ara
                        </Button>
                      </InputGroup>
                    </Col>

                    <Col md={3}>
                      <Form.Select
                        value={filters.documentType}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            documentType: e.target.value,
                          }))
                        }
                      >
                        {DOCUMENT_TYPES.map((item) => (
                          <option key={item.value} value={item.value}>
                            Tip: {item.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>

                    <Col md={3}>
                      <Form.Select
                        value={filters.status}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            status: e.target.value,
                          }))
                        }
                      >
                        {DOCUMENT_STATUSES.map((item) => (
                          <option key={item.value} value={item.value}>
                            Durum: {item.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>

                    <Col md={2} className="d-flex align-items-center">
                      <Form.Check
                        type="switch"
                        label="Alt klasörler"
                        checked={filters.includeSubFolders}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            includeSubFolders: e.target.checked,
                          }))
                        }
                      />
                    </Col>
                  </Row>
                </Form>

                {documentLoading ? (
                  <div className="text-center py-5">
                    <Spinner />
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table hover bordered size="sm" className="align-middle">
                      <thead>
                        <tr>
                          <th>Doküman Kodu</th>
                          <th>Doküman Adı</th>
                          <th>Tip</th>
                          <th>Revizyon</th>
                          <th>Durum</th>
                          <th>Klasör</th>
                          <th>İndirme</th>
                          <th style={{ width: 180 }}>İşlem</th>
                        </tr>
                      </thead>

                      <tbody>
                        {documents.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="text-center text-muted py-4">
                              Doküman bulunamadı.
                            </td>
                          </tr>
                        ) : (
                          documents.map((document) => (
                            <tr key={document._id}>
                              <td>
                                <strong>{document.documentCode || "-"}</strong>
                              </td>
                              <td>{document.documentName}</td>
                              <td>{document.documentType}</td>
                              <td>Rev.{document.currentRevisionNo}</td>
                              <td>
                                <Badge bg={STATUS_BADGE[document.status] || "secondary"}>
                                  {document.status}
                                </Badge>
                              </td>
                              <td>{document.folderId?.path || document.folderId?.name || "-"}</td>
                              <td>{document.downloadCount || 0}</td>
                              <td>
                                <ButtonGroup size="sm">
                                  <Button
                                    variant="outline-primary"
                                    onClick={() =>
                                      navigate(`/QualityDocumentDetailPage/${document._id}`)
                                    }
                                  >
                                    Detay
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    onClick={() => handleDeleteDocument(document)}
                                  >
                                    Pasif
                                  </Button>
                                </ButtonGroup>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}

                <div className="d-flex justify-content-between align-items-center mt-3">
                  <small className="text-muted">
                    Sayfa {pagination.currentPage} / {pagination.totalPages}
                  </small>

                  <ButtonGroup size="sm">
                    <Button
                      variant="outline-secondary"
                      disabled={pagination.currentPage <= 1}
                      onClick={() => loadDocuments(pagination.currentPage - 1)}
                    >
                      Önceki
                    </Button>
                    <Button
                      variant="outline-secondary"
                      disabled={pagination.currentPage >= pagination.totalPages}
                      onClick={() => loadDocuments(pagination.currentPage + 1)}
                    >
                      Sonraki
                    </Button>
                  </ButtonGroup>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Modal show={folderModal.show} onHide={closeFolderModal} centered>
          <Form onSubmit={submitFolder}>
            <Modal.Header closeButton>
              <Modal.Title>
                {folderModal.mode === "edit" ? "Klasör Düzenle" : "Klasör Ekle"}
              </Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Klasör Adı</Form.Label>
                <Form.Control
                  value={folderModal.name}
                  onChange={(e) =>
                    setFolderModal((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Üst Klasör</Form.Label>
                <Form.Select
                  value={folderModal.parentId || ""}
                  onChange={(e) =>
                    setFolderModal((prev) => ({
                      ...prev,
                      parentId: e.target.value || null,
                    }))
                  }
                >
                  <option value="">Ana klasör</option>
                  {folderOptions.map((folder) => (
                    <option key={folder.value} value={folder.value}>
                      {folder.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Açıklama</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={folderModal.description}
                  onChange={(e) =>
                    setFolderModal((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Sıra</Form.Label>
                <Form.Control
                  type="number"
                  value={folderModal.order}
                  onChange={(e) =>
                    setFolderModal((prev) => ({
                      ...prev,
                      order: e.target.value,
                    }))
                  }
                />
              </Form.Group>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={closeFolderModal}>
                Vazgeç
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? "Kaydediliyor..." : "Kaydet"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={documentModal.show} onHide={closeDocumentModal} size="lg" centered>
          <Form onSubmit={submitDocument}>
            <Modal.Header closeButton>
              <Modal.Title>Doküman Yükle</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Klasör</Form.Label>
                    <Form.Select
                      value={documentModal.folderId}
                      onChange={(e) =>
                        setDocumentModal((prev) => ({
                          ...prev,
                          folderId: e.target.value,
                        }))
                      }
                      required
                    >
                      <option value="">Klasör seç</option>
                      {folderOptions.map((folder) => (
                        <option key={folder.value} value={folder.value}>
                          {folder.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Doküman Kodu</Form.Label>
                    <Form.Control
                      value={documentModal.documentCode}
                      onChange={(e) =>
                        setDocumentModal((prev) => ({
                          ...prev,
                          documentCode: e.target.value,
                        }))
                      }
                      placeholder="PR07-FR24"
                    />
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Tip</Form.Label>
                    <Form.Select
                      value={documentModal.documentType}
                      onChange={(e) =>
                        setDocumentModal((prev) => ({
                          ...prev,
                          documentType: e.target.value,
                        }))
                      }
                    >
                      {DOCUMENT_TYPES.filter((item) => item.value).map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Doküman Adı</Form.Label>
                <Form.Control
                  value={documentModal.documentName}
                  onChange={(e) =>
                    setDocumentModal((prev) => ({
                      ...prev,
                      documentName: e.target.value,
                    }))
                  }
                  required
                />
              </Form.Group>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Durum</Form.Label>
                    <Form.Select
                      value={documentModal.status}
                      onChange={(e) =>
                        setDocumentModal((prev) => ({
                          ...prev,
                          status: e.target.value,
                        }))
                      }
                    >
                      {DOCUMENT_STATUSES.filter((item) => item.value).map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Revizyon No</Form.Label>
                    <Form.Control
                      type="number"
                      value={documentModal.revisionNo}
                      onChange={(e) =>
                        setDocumentModal((prev) => ({
                          ...prev,
                          revisionNo: e.target.value,
                        }))
                      }
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Revizyon Tarihi</Form.Label>
                    <Form.Control
                      type="date"
                      value={documentModal.revisionDate}
                      onChange={(e) =>
                        setDocumentModal((prev) => ({
                          ...prev,
                          revisionDate: e.target.value,
                        }))
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Açıklama</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={documentModal.description}
                  onChange={(e) =>
                    setDocumentModal((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Revizyon Notu</Form.Label>
                <Form.Control
                  value={documentModal.changeNote}
                  onChange={(e) =>
                    setDocumentModal((prev) => ({
                      ...prev,
                      changeNote: e.target.value,
                    }))
                  }
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Dosya</Form.Label>
                <Form.Control
                  type="file"
                  onChange={(e) =>
                    setDocumentModal((prev) => ({
                      ...prev,
                      file: e.target.files?.[0] || null,
                    }))
                  }
                  required
                />
              </Form.Group>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={closeDocumentModal}>
                Vazgeç
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? "Yükleniyor..." : "Yükle"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    </Layout>
  );
}

export default QualityFileManagerPage;